import api from './api';
import { ApiResponse, AuthTokens, LoginCredentials, User } from '../models';

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  password_confirm: string;
  name: string;
  department?: string;
  phone?: string;
}

export const authService = {
  async register(data: RegisterData): Promise<User> {
    const response = await api.post<ApiResponse<User>>('/users/', data);
    return response.data.data;
  },
  async login(credentials: LoginCredentials): Promise<AuthTokens> {
    const response = await api.post<ApiResponse<AuthTokens>>('/auth/login/', credentials);
    const tokens = response.data.data;

    // Store tokens
    localStorage.setItem('access_token', tokens.access);
    localStorage.setItem('refresh_token', tokens.refresh);

    return tokens;
  },

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout/');
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  },

  async refreshToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token');
    }

    const response = await api.post<ApiResponse<{ access: string }>>('/auth/refresh/', {
      refresh: refreshToken,
    });

    const { access } = response.data.data;
    localStorage.setItem('access_token', access);

    return access;
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get<ApiResponse<User>>('/users/me/');
    return response.data.data;
  },

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await api.patch<ApiResponse<User>>('/users/me/', data);
    return response.data.data;
  },

  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  },

  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  },

  // WeDMS 토큰 연동
  async connectWeDMS(wedms_username: string, wedms_password: string): Promise<{ wedms_username: string; has_wedms_token: boolean }> {
    const response = await api.post<ApiResponse<{ wedms_username: string; has_wedms_token: boolean }>>('/users/wedms-token/', {
      wedms_username,
      wedms_password,
    });
    return response.data.data;
  },

  async disconnectWeDMS(): Promise<void> {
    await api.delete('/users/wedms-token/');
  },

  // WeDMS 연동 후 대기 중인 항목 자동 처리
  async processPendingWeDMS(): Promise<{
    recordings: { started: number; failed: number; skipped: number };
    templates: { started: number; failed: number; skipped: number };
    message: string;
    errors?: string[];
  }> {
    const response = await api.post<ApiResponse<{
      recordings: { started: number; failed: number; skipped: number };
      templates: { started: number; failed: number; skipped: number };
    }>>('/users/process-pending-wedms/');
    return {
      ...response.data.data,
      message: response.data.message || '',
      errors: (response.data as { errors?: string[] }).errors,
    };
  },
};
